export type AuthStorageKind = 'local' | 'session';

const AUTH_STORAGE_KEY = 'authStorage';
const AUTH_TOKEN_KEY = 'authToken';
const AUTH_USER_KEY = 'user';

function getStorage(kind: AuthStorageKind): Storage {
  return kind === 'local' ? localStorage : sessionStorage;
}

function normalizeKind(value: string | null): AuthStorageKind | null {
  return value === 'local' || value === 'session' ? value : null;
}

export function getActiveAuthStorageKind(): AuthStorageKind | null {
  const markedKind = normalizeKind(localStorage.getItem(AUTH_STORAGE_KEY) || sessionStorage.getItem(AUTH_STORAGE_KEY));
  if (markedKind) {
    return markedKind;
  }

  const hasLocalSession = Boolean(localStorage.getItem(AUTH_TOKEN_KEY) && localStorage.getItem(AUTH_USER_KEY));
  const hasSessionSession = Boolean(sessionStorage.getItem(AUTH_TOKEN_KEY) && sessionStorage.getItem(AUTH_USER_KEY));

  if (hasLocalSession && hasSessionSession) {
    return null;
  }

  if (hasLocalSession) {
    return 'local';
  }

  if (hasSessionSession) {
    return 'session';
  }

  return null;
}

export function getActiveAuthStorage(): Storage | null {
  const kind = getActiveAuthStorageKind();
  return kind ? getStorage(kind) : null;
}

export function getStoredAuthToken(): string | null {
  return getActiveAuthStorage()?.getItem(AUTH_TOKEN_KEY) || null;
}

export function getStoredAuthUserJson(): string | null {
  return getActiveAuthStorage()?.getItem(AUTH_USER_KEY) || null;
}

export function getStoredAuthUser<T = Record<string, unknown>>(): T | null {
  const rawUser = getStoredAuthUserJson();
  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser) as T;
  } catch {
    return null;
  }
}

export function clearAuthSession(): void {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  localStorage.removeItem(AUTH_STORAGE_KEY);
  sessionStorage.removeItem(AUTH_TOKEN_KEY);
  sessionStorage.removeItem(AUTH_USER_KEY);
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
}

export function setAuthSession(kind: AuthStorageKind, token: string, userJson: string): void {
  clearAuthSession();
  const storage = getStorage(kind);
  storage.setItem(AUTH_TOKEN_KEY, token);
  storage.setItem(AUTH_USER_KEY, userJson);
  localStorage.setItem(AUTH_STORAGE_KEY, kind);
  sessionStorage.setItem(AUTH_STORAGE_KEY, kind);
}

export function syncStoredAuthUser(userJson: string | null): void {
  const storage = getActiveAuthStorage();
  if (!userJson) {
    clearAuthSession();
    return;
  }

  if (storage) {
    storage.setItem(AUTH_USER_KEY, userJson);
  }
}
